import { useCallback } from 'react'

import bs58 from 'bs58'
import posthog from 'posthog-js'

import * as anchor from '@coral-xyz/anchor'
import { createAssociatedTokenAccountInstruction, getAccount, getAssociatedTokenAddress } from '@solana/spl-token'
import { useWallet } from '@solana/wallet-adapter-react'
import { useAnchorWallet } from '@solana/wallet-adapter-react'
import { ComputeBudgetProgram, PublicKey, SendTransactionError, Transaction } from '@solana/web3.js'

import {
    ORDER_BOOK_PROGRAM_ID,
    connection,
    getHighPriorityConnection,
    initializeOrderBookProgram,
} from '@src/chains/solana/anchor/setup'
import { findProgramAddress } from '@src/chains/solana/utils'
import { IOrderDirectionParameter, IOrderFundingParameter } from '@src/types/order'
import { Address } from '@src/types/token'
import { solanaLogger as logger } from '@src/utils/logger'

/**
 * Parameters for placing an order on the Solana order book
 */
type PlaceOrderParams = {
    sourceSellAmount: anchor.BN // Amount of source tokens to send
    minSellAmount: anchor.BN // Amount of source tokens to send
    dstTokenMint: number[] // Destination token address as byte array
    dstBuyAmount: anchor.BN // Amount of destination tokens to receive
    eid: number // LayerZero chain ID for the destination
    targetAddress: number[] // Target address as byte array
    bondAssetMint: PublicKey // Token mint address
    bondAmount: anchor.BN // Amount of bond tokens
    bondFee: number // Fee for bond tokens
}

/**
 * Required accounts for the place order instruction
 */
type PlaceOrderAccounts = {
    authority: PublicKey // User's wallet public key
    oapp: PublicKey // Tristero OApp account
    adminPanel: PublicKey // Admin panel account
    tokenMint: PublicKey // Token mint address
    tokenAccount: PublicKey // User's associated token account
    matchAccount: PublicKey // Match account (currently user's wallet)
    stakingAccount: PublicKey // Staking account for the token
    order: PublicKey // Order account PDA
}

/**
 * Hook for placing cross-chain orders on Solana.
 *
 * Exports:
 * - getPlaceOrderData: Returns the constructed (unsigned) transaction.
 * - placeOrder: Signs and sends the order transaction.
 *
 * The getPlaceOrderData function accepts a publicKey parameter so that you can
 * pass a dummy key when simulating gas fees or the actual wallet key when placing the order.
 */
const usePlaceOrder = () => {
    const { signTransaction } = useWallet()
    const wallet = useAnchorWallet()

    /**
     * Generates required program addresses for order placement.
     * (Based on the current order count and token mint.)
     */
    const getProgramAddresses = useCallback((orderCount: number, tokenMint: PublicKey) => {
        const adminPanel = findProgramAddress('admin_panel', ORDER_BOOK_PROGRAM_ID).publicKey
        // Convert orderCount to an 8-byte buffer (matching Python implementation)
        const orderCountBuffer = Buffer.alloc(8)
        orderCountBuffer.writeBigUInt64BE(BigInt(orderCount))
        const orderPda = findProgramAddress('order', ORDER_BOOK_PROGRAM_ID, [orderCountBuffer]).publicKey
        const tristeroOapp = findProgramAddress('TristeroOapp', ORDER_BOOK_PROGRAM_ID).publicKey
        const stakingPanel = findProgramAddress('staking_account', ORDER_BOOK_PROGRAM_ID, [tokenMint]).publicKey

        return {
            adminPanel,
            orderPda,
            tristeroOapp,
            stakingPanel,
        }
    }, [])

    /**
     * Constructs the versioned order transaction.
     *
     * @param orderDir - Order direction details (e.g. token addresses and destination chain)
     * @param orderFunding - Funding details (amounts and token addresses)
     * @param targetAddress - Destination address for the order
     * @param ownerPublicKey - Public key to use as authority (dummy for simulation, wallet key for live orders)
     * @returns An object containing:
     *    - finalTx: The constructed Transaction with additional instructions.
     *    - blockHash: The recent blockhash used.
     *    - versionedTransaction: The VersionedTransaction built from finalTx.
     */
    const getPlaceOrderData = async (
        orderDir: IOrderDirectionParameter,
        orderFunding: IOrderFundingParameter,
        targetAddress: Address,
        ownerPublicKey: PublicKey
    ): Promise<{ finalTx: Transaction; blockHash: string; versionedTransaction: anchor.web3.VersionedTransaction }> => {
        // Initialize the Anchor program with the provided owner public key.
        const program = initializeOrderBookProgram(ownerPublicKey as unknown as anchor.Wallet)

        // Get admin panel data and current order count.
        // @ts-ignore
        const adminPanelPda = getProgramAddresses(0, new PublicKey(orderFunding.bondAsset)).adminPanel
        // @ts-ignore
        const adminPanelAccount = await program.account.adminPanel.fetch(adminPanelPda)
        const orderCount = adminPanelAccount.orderCount

        // Get associated token account for the source token.
        const tokenMint = new PublicKey(orderDir.srcAsset)
        const tokenAccount = await getAssociatedTokenAddress(tokenMint, ownerPublicKey)

        // For destination tokens, ensure an Associated Token Account (ATA) exists.
        // If not, create an instruction for ATA creation.
        let ataInstruction = undefined
        try {
            const dstTokenMint = new PublicKey(orderDir.dstAsset)
            const dstTokenAccount = await getAssociatedTokenAddress(dstTokenMint, ownerPublicKey)
            try {
                await getAccount(connection, dstTokenAccount)
            } catch (e) {
                ataInstruction = createAssociatedTokenAccountInstruction(
                    ownerPublicKey, // payer
                    dstTokenAccount, // ATA
                    ownerPublicKey, // owner
                    dstTokenMint // mint
                )
                logger.debug('Adding ATA creation instruction for Solana destination token')
            }
        } catch (e) {
            logger.debug('Destination token is not a Solana address')
        }

        // Get additional program addresses based on current order count and token mint.
        const addresses = getProgramAddresses(orderCount, tokenMint)
        const marketMaker = new PublicKey(
            process.env.REACT_APP_SOL_MARKET_MAKER || 'DDic8gLwFSxsnwxgVnFDjED7dL1WAn6CyDVcFE418Zdp'
        )

        const accounts: PlaceOrderAccounts = {
            authority: ownerPublicKey,
            oapp: addresses.tristeroOapp,
            adminPanel: addresses.adminPanel,
            tokenMint: tokenMint,
            tokenAccount: tokenAccount,
            matchAccount: marketMaker,
            stakingAccount: addresses.stakingPanel,
            order: addresses.orderPda,
        }

        // Process destination token address (base58 or hex) and target address.
        const dstTokenAddress = orderDir.dstAsset.startsWith('0x') ? orderDir.dstAsset.slice(2) : orderDir.dstAsset
        const dstTokenBytes = orderDir.dstAsset.startsWith('0x')
            ? Buffer.from(dstTokenAddress, 'hex')
            : bs58.decode(dstTokenAddress)
        const targetAddressBytes = targetAddress.startsWith('0x')
            ? Buffer.from(targetAddress.slice(2), 'hex')
            : bs58.decode(targetAddress)

        const params: PlaceOrderParams = {
            sourceSellAmount: new anchor.BN(orderFunding.srcQuantity.toString()),
            minSellAmount: new anchor.BN(orderFunding.srcQuantity.toString()),
            dstTokenMint: Array.from(dstTokenBytes),
            dstBuyAmount: new anchor.BN(orderFunding.dstQuantity.toString()),
            eid: orderDir.dstLzc,
            targetAddress: Array.from(targetAddressBytes),
            bondAssetMint: new PublicKey(orderFunding.bondAsset),
            bondAmount: new anchor.BN(orderFunding.bondAmount.toString()),
            bondFee: orderFunding.bondFee,
        }

        // Add compute budget instructions.
        const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
            units: 65000,
        })
        const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: 7500000,
        })

        // Construct the placeOrder instruction using the Anchor program.
        const tx = await program.methods.placeOrder(params).accounts(accounts).transaction()

        // Build the final transaction.
        const finalTx = new Transaction()
        finalTx.add(modifyComputeUnits)
        finalTx.add(addPriorityFee)
        if (ataInstruction) {
            finalTx.add(ataInstruction)
        }
        finalTx.add(tx)

        // Retrieve a recent blockhash.
        const { blockhash } = await connection.getLatestBlockhash('confirmed')
        finalTx.recentBlockhash = blockhash
        finalTx.feePayer = ownerPublicKey

        // Convert the transaction into a versioned transaction.
        const transactionMessageV0 = new anchor.web3.TransactionMessage({
            payerKey: ownerPublicKey,
            recentBlockhash: blockhash,
            instructions: finalTx.instructions,
        }).compileToV0Message()
        const versionedTransaction = new anchor.web3.VersionedTransaction(transactionMessageV0)

        return { finalTx, blockHash: blockhash, versionedTransaction }
    }

    /**
     * Places a cross-chain order on Solana.
     *
     * This function uses getPlaceOrderData to construct the transaction,
     * then signs and sends it.
     *
     * @param orderDir - Order direction details.
     * @param orderFunding - Order funding details.
     * @param targetAddress - Destination address for the order.
     * @returns The transaction signature.
     */
    const placeOrder = useCallback(
        async (
            orderDir: IOrderDirectionParameter,
            orderFunding: IOrderFundingParameter,
            targetAddress: Address
        ): Promise<string> => {
            if (!wallet?.publicKey) {
                logger.error('Wallet not connected')
                throw new Error('Wallet not connected')
            }

            try {
                logger.info('Initiating Solana order placement', {
                    orderDir,
                    orderFunding,
                    targetAddress,
                })

                const highPriorityConnection = getHighPriorityConnection()

                // Get latest blockhash first
                const { blockhash, lastValidBlockHeight } = await highPriorityConnection.getLatestBlockhash('finalized')

                // Get transaction data with the obtained blockhash
                const { finalTx: tx } = await getPlaceOrderData(orderDir, orderFunding, targetAddress, wallet.publicKey)

                // Update transaction with our pre-fetched blockhash
                tx.recentBlockhash = blockhash
                tx.feePayer = wallet.publicKey

                if (!signTransaction) throw new Error('Wallet does not support signing')
                const signed = await signTransaction(tx)

                // Send with retry logic
                const signature = await highPriorityConnection.sendRawTransaction(signed.serialize(), {
                    skipPreflight: false,
                    preflightCommitment: 'confirmed',
                    maxRetries: 3, // Add retry attempts
                })

                // Wait for confirmation
                const confirmation = await highPriorityConnection.confirmTransaction({
                    signature,
                    blockhash,
                    lastValidBlockHeight,
                })

                if (confirmation.value.err) {
                    throw new Error(`Transaction failed: ${confirmation.value.err}`)
                }

                logger.info('Order placement transaction sent', { signature })
                return signature
            } catch (error) {
                if (error instanceof SendTransactionError) {
                    logger.error('[orderbook][sol] Transaction simulation failed. Details:', {
                        error: error.message,
                        logs: error.logs,
                    })

                    posthog.capture('solana_order_failed', {
                        error_type: 'SendTransactionError',
                        error_message: error.message,
                        error_logs: error.logs,
                        order_details: {
                            source_token: orderDir.srcAsset,
                            destination_token: orderDir.dstAsset,
                            source_amount: orderFunding.srcQuantity.toString(),
                            destination_amount: orderFunding.dstQuantity.toString(),
                            destination_chain_id: orderDir.dstLzc,
                        },
                    })
                } else {
                    posthog.capture('solana_order_failed', {
                        error_type: error instanceof Error ? error.constructor.name : 'Unknown',
                        error_message: error instanceof Error ? error.message : String(error),
                        order_details: {
                            source_token: orderDir.srcAsset,
                            destination_token: orderDir.dstAsset,
                            source_amount: orderFunding.srcQuantity.toString(),
                            destination_amount: orderFunding.dstQuantity.toString(),
                            destination_chain_id: orderDir.dstLzc,
                        },
                    })
                }
                logger.error('Order placement failed', {
                    error: error instanceof Error ? error.message : String(error),
                    orderDir,
                    orderFunding,
                })
                throw error
            }
        },
        [wallet, signTransaction, getPlaceOrderData]
    )

    return { placeOrder, getPlaceOrderData }
}

export default usePlaceOrder
